import {useCallback} from 'react';
import useUpdatePayinSessionMutation from 'src/api/graphql/useUpdatePayinSessionMutation';
import {useSelector} from 'src/v2/state/store';
import type {
  OpenapiPostV1CryptoInternalPayinSessionUpdateInputRefundParamsRefundDataCryptocurrencyEnum as CryptocurrencyEnum,
  OpenapiPostV1CryptoInternalPayinSessionUpdateInputRefundParamsRefundDataDestinationNetworkEnum as DestinationNetworkEnum,
} from '@stripe-internal/data-v1/schema/types';

type UpdateRefundDetailsParams = {
  refund?: {
    address: string | null;
    cryptocurrency: CryptocurrencyEnum;
    network: DestinationNetworkEnum;
  };
  customerEmail?: string;
};

export const useUpdateRefundDetails = (): [
  (params: UpdateRefundDetailsParams) => Promise<void>,
  {loading: boolean},
] => {
  const [updatePayinSessionMutation, {loading}] =
    useUpdatePayinSessionMutation();
  const sessionId = useSelector((state) => state.id);
  const clientSecret = useSelector((state) => state.clientSecret);

  const updateRefundDetails = useCallback(
    async ({refund, customerEmail}: UpdateRefundDetailsParams) => {
      const result = await updatePayinSessionMutation({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_payin_session: sessionId,
            ...(refund
              ? {
                  refund_params: {
                    refund_data: {
                      refund_address: refund.address ?? '',
                      cryptocurrency: refund.cryptocurrency,
                      destination_network: refund.network,
                    },
                  },
                }
              : {}),
            ...(customerEmail !== undefined
              ? {customer_email: customerEmail}
              : {}),
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      if (!result.data?.v1CryptoInternalPayinSessionUpdate) {
        throw new Error('Failed to update payin session');
      }
    },
    [sessionId, clientSecret, updatePayinSessionMutation],
  );

  return [updateRefundDetails, {loading}];
};
